import { createInstancedRenderer } from "./instancedRenderer";
import { paintFill } from "../canvas2d/paintFill";

// Instance: [x0, top0, x1, top1], [bottom0, bottom1] in data units, [r, g, b, a]
const FILL_ATTRIBUTE_COMPONENTS = [4, 2, 4];

// Each segment is a trapezoid between a segment of the upper line and the
// segment of the lower line under it; neighbours share their vertical edges
const FILL_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec4 aTop;
layout(location = 2) in vec2 aBottom;
layout(location = 3) in vec4 aColor;

uniform vec2 uViewport;
uniform vec2 uX;
uniform vec2 uY;

out vec4 vColor;

void main() {
  float x = mix(aTop.x, aTop.z, aCorner.x) * uX.x + uX.y;
  float top = mix(aTop.y, aTop.w, aCorner.x) * uY.x + uY.y;
  float bottom = mix(aBottom.x, aBottom.y, aCorner.x) * uY.x + uY.y;
  vec2 clip = vec2(x, mix(top, bottom, aCorner.y)) / uViewport * 2.0 - 1.0;

  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vColor = aColor;
}
`;

/**
 * Instanced background between two polylines in a flat colour: the fill of
 * indicator channels (Bollinger Bands, Envelopes…).
 */
export const createFillRenderer = () => {
  const renderer = createInstancedRenderer({
    vertexShader: FILL_VERTEX_SHADER,
    uniformNames: ["uViewport", "uX", "uY"],
    attributeComponents: FILL_ATTRIBUTE_COMPONENTS,
  });

  return {
    floatsPerInstance: renderer.floatsPerInstance,
    init: renderer.init,
    release: renderer.release,
    setInstances: renderer.setInstances,
    updateInstances: renderer.updateInstances,

    /**
     * @param {object} params
     * @param {number[]} params.viewport   - [width, height] of the canvas, device px
     * @param {number[]} params.xTransform - [scale, offset]: data x → device px
     * @param {number[]} params.yTransform - [scale, offset]: data y → device px
     */
    draw: (params) => {
      const { viewport, xTransform, yTransform } = params;

      renderer.draw(
        (gl, uniforms) => {
          gl.uniform2fv(uniforms.uViewport, viewport);
          gl.uniform2fv(uniforms.uX, xTransform);
          gl.uniform2fv(uniforms.uY, yTransform);
        },
        (context, instances) => paintFill(context, instances, renderer.floatsPerInstance, params),
      );
    },
  };
};
